import ConfirmationDialog from '@components/dialogs/ConfirmationDialog'
import { DialogTitle } from '@components/titles'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  Button,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Slide,
  TextField,
  Typography,
} from '@mui/material'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import { Exam, Site } from '@services/api'
import { FindExamsQuery } from '@services/dtos'
import {
  doDeleteAcquisitionProtocols,
  doFindAcquisitionProtocols,
} from '@state/reducers/acqProtocolReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import theme from '@utils/theme'
import { debounce } from 'lodash'
import { EditIcon, Trash2 } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import AcquisitionProtocolForm from '../../../components/forms/AcquisitionProtocolForm'
import { findAll } from '../../../state/thunks/procedureThunk'

const Transition = React.forwardRef(function Transition(
  props: any,
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />
})

const AcquisitionProtocols: React.FC = () => {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const [isFetching, setIsFetching] = useState(false)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingAcquisitionProtocol, setEditingAcquisitionProtocol] =
    useState<any>(undefined)
  const [selectedExams, setSelectedExams] = useState<Exam[]>([])
  const [isExamsLoading, setIsExamsLoading] = useState(false)
  const { acquisitionProtocols } = useAppSelector(
    ({ acProtocols, procedure }) => ({
      acquisitionProtocols: acProtocols.acquisitionProtocols,
      exams: procedure.exams,
    }),
  )
  const [examFilterValue, setexamFilterValue] = useState('')
  const [selectedSiteId, setSelectedSiteId] = useState(-1)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false)

  const handleOpenPdf = (url: string) => {
    setPdfUrl(url)
    setIsPdfModalOpen(true)
  }

  const handleClosePdf = () => {
    setIsPdfModalOpen(false)
    setPdfUrl(null)
  }

  const user = useAppSelector(({ auth }) => auth.user)

  const sites = useMemo(() => {
    return (user as unknown & { sites: Site[] })?.sites
  }, [user])

  useEffect(() => {
    setIsFetching(true)
    dispatch(
      doFindAcquisitionProtocols({
        limit: 40,
        page: 0,
        examIds: selectedExams.map((e) => e.id),
        siteIds: [selectedSiteId],
      }),
    ).finally(() => {
      setIsFetching(false)
    })
  }, [selectedExams, selectedSiteId])

  const handleDeleteProtocol = (id: number) => {
    dispatch(doDeleteAcquisitionProtocols(id)).then(() => {
      dispatch(
        enqueueSnackbar({
          message: 'Le protocole de réalisation a été supprimé avec succès',
          options: { variant: 'success' },
        }),
      )
    })
  }

  const searchExams = useMemo(
    () =>
      debounce((query: FindExamsQuery) => {
        setIsExamsLoading(true)
        dispatch(findAll(query))
          .unwrap()
          .finally(() => {
            setIsExamsLoading(false)
          })
      }, 500),
    [],
  )
  const handleSearchExams = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setexamFilterValue(value)
  }

  useEffect(() => {
    const q: FindExamsQuery = {
      search: examFilterValue,
    }
    if (selectedSiteId !== -1) {
      q.siteIds = [selectedSiteId]
    }
    searchExams(q)
  }, [selectedSiteId, examFilterValue])

  useEffect(() => {
    if (sites?.length) {
      setSelectedSiteId(sites[0].id)
    }
  }, [sites])

  const handleAddClick = () => {
    setEditingAcquisitionProtocol(undefined)
    setIsFormOpen(true)
  }

  const handleEditClick = (protocol: any) => {
    setEditingAcquisitionProtocol(protocol)
    setIsFormOpen(true)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'title' }),
        width: 250,
      },
      {
        field: 'description',
        headerName: intl.formatMessage({ id: 'description' }),
        flex: 1,
      },
      {
        field: 'createdAt',
        headerName: intl.formatMessage({ id: 'createdAt' }),
        width: 150,
        renderCell: (params) =>
          new Date(params.row.createdAt).toLocaleDateString(),
      },
      {
        field: 'actions',
        align: 'right',
        width: 150,
        headerName: intl.formatMessage({ id: 'actions' }),
        renderCell: (params) => (
          <div className="flex space-x-1">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
            >
              <EditIcon />
            </IconButton>

            {params.row.pdfUrl && (
              <IconButton
                size="small"
                onClick={() =>
                  handleOpenPdf(
                    `${process.env.REACT_APP_API_URL}/${params.row.pdfUrl}`,
                  )
                }
              >
                <i className="fas fa-file-pdf text-red-600"></i>
              </IconButton>
            )}

            <ConfirmationDialog
              title="Supprimer le protocole de réalisation"
              description={`Êtes-vous sûr de vouloir supprimer le protocole de réalisation ${params.row.title} ?`}
              response={() => handleDeleteProtocol(params.row.id)}
            >
              {(showDialog) => (
                <IconButton size="small" color="error" onClick={showDialog}>
                  <Trash2 />
                </IconButton>
              )}
            </ConfirmationDialog>
          </div>
        ),
      },
    ],
    [intl],
  )

  return (
    <div className="flex flex-col gap-4 w-full h-[84vh] py-2">
      <div className="flex items-center justify-between">
        {selectedSiteId !== -1 && (
          <TextField
            size="small"
            label="Site"
            select
            sx={{
              width: 400,
              backgroundColor: 'white',
            }}
            defaultValue={selectedSiteId}
            onChange={(ev) => {
              const value = ev.target.value
              setSelectedSiteId(value as any as number)
            }}
          >
            {sites?.map((site: Site) => (
              <MenuItem key={site.id} value={site.id}>
                {site.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        <div className="w-1/4 flex justify-end">
          <Button
            style={{
              backgroundColor: theme.palette.primary.main,
            }}
            variant="contained"
            onClick={handleAddClick}
          >
            <i className="fas fa-plus mr-2"></i> Ajouter
          </Button>
        </div>
      </div>

      <div className="h-full bg-white rounded-lg">
        <DataGridPro
          rows={acquisitionProtocols.datas}
          columns={columns}
          loading={isFetching}
          pageSize={20}
          rowsPerPageOptions={[20]}
          page={acquisitionProtocols.page}
          rowCount={acquisitionProtocols.totalCount}
          pagination
          paginationMode="server"
          sx={{
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: 'rgba(235, 235, 245, 0.9)',
            },
            '& .MuiDataGrid-row:nth-of-type(even)': {
              backgroundColor: 'white',
            },
            '& .MuiDataGrid-root': {
              borderRadius: '20px',
              overflowY: 'scroll',
            },
          }}
          onPageChange={(page) => {
            setIsFetching(true)
            dispatch(doFindAcquisitionProtocols({ page, limit: 20 })).finally(
              () => {
                setIsFetching(false)
              },
            )
          }}
        />
      </div>
      <Dialog
        open={isFormOpen}
        onClose={handleCloseForm}
        fullWidth
        maxWidth="md"
        TransitionComponent={Transition}
      >
        <DialogContent>
          <Typography variant="h6" fontWeight="bold" mb={4}>
            {editingAcquisitionProtocol
              ? 'Modifier un protocole'
              : 'Ajouter un protocole'}
          </Typography>
          <AcquisitionProtocolForm
            editingAcquisitionProtocol={editingAcquisitionProtocol}
            onClose={handleCloseForm}
            defaultSiteId={selectedSiteId}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={isPdfModalOpen}
        onClose={handleClosePdf}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle onClose={handleClosePdf} title="Fiche protocole" />
        <DialogContent
          style={{
            height: '90vh',
            padding: 0,
            margin: 0,
            display: 'flex',
          }}
        >
          {pdfUrl && (
            <iframe
              src={`${pdfUrl}#toolbar=0`}
              width="100%"
              height="600px"
              title="PDF Viewer"
              style={{ width: '100%', height: '100%', border: 'none', flex: 1 }}
            ></iframe>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AcquisitionProtocols
